import { Drawer, Switch, Input, Button, Typography, InputNumber } from 'antd';
import { HolderOutlined } from '@ant-design/icons';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ColumnSetting } from '@/hooks/useColumnSettings';

const { Text } = Typography;

interface ColumnSettingsDrawerProps {
  open: boolean;
  onClose: () => void;
  settings: ColumnSetting[];
  onSetVisible: (key: string, visible: boolean) => void;
  onSetDisplayTitle: (key: string, title: string) => void;
  onSetWidth: (key: string, width?: number) => void;
  onReorder: (fromIndex: number, toIndex: number) => void;
  onReset: () => void;
}

function SortableRow({
  setting,
  onSetVisible,
  onSetDisplayTitle,
  onSetWidth,
}: {
  setting: ColumnSetting;
  onSetVisible: (key: string, visible: boolean) => void;
  onSetDisplayTitle: (key: string, title: string) => void;
  onSetWidth: (key: string, width?: number) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: setting.key,
  });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 0',
        borderBottom: '1px solid #f0f0f0',
      }}
    >
      <span {...attributes} {...listeners} style={{ cursor: 'grab', color: '#ccc' }}>
        <HolderOutlined />
      </span>
      <Switch
        size="small"
        checked={setting.visible}
        onChange={(v) => onSetVisible(setting.key, v)}
      />
      <Input
        size="small"
        value={setting.displayTitle}
        onChange={(e) => onSetDisplayTitle(setting.key, e.target.value)}
        onBlur={() => {
          if (!setting.displayTitle.trim()) {
            onSetDisplayTitle(setting.key, setting.title);
          }
        }}
        status={!setting.displayTitle.trim() ? 'error' : undefined}
        style={{ flex: 1 }}
        placeholder={setting.title}
      />
      <InputNumber
        size="small"
        min={60}
        max={600}
        step={10}
        value={setting.width}
        placeholder="120"
        style={{ width: 86 }}
        onChange={(v) => onSetWidth(setting.key, typeof v === 'number' ? v : undefined)}
      />
      {setting.displayTitle !== setting.title && (
        <Text type="secondary" style={{ fontSize: 11 }}>
          (gốc: {setting.title})
        </Text>
      )}
    </div>
  );
}

export function ColumnSettingsDrawer({
  open,
  onClose,
  settings,
  onSetVisible,
  onSetDisplayTitle,
  onSetWidth,
  onReorder,
  onReset,
}: ColumnSettingsDrawerProps) {
  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = settings.findIndex((s) => s.key === active.id);
    const newIndex = settings.findIndex((s) => s.key === over.id);
    onReorder(oldIndex, newIndex);
  };

  return (
    <Drawer
      title="Tuỳ chỉnh cột"
      open={open}
      onClose={onClose}
      width={360}
      extra={
        <Button size="small" onClick={onReset}>
          Đặt lại
        </Button>
      }
    >
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
        Kéo để sắp xếp · Bật/tắt ẩn hiện · Đổi tên · Chỉnh rộng cột (px)
      </Text>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={settings.map((s) => s.key)} strategy={verticalListSortingStrategy}>
          {settings.map((s) => (
            <SortableRow
              key={s.key}
              setting={s}
              onSetVisible={onSetVisible}
              onSetDisplayTitle={onSetDisplayTitle}
              onSetWidth={onSetWidth}
            />
          ))}
        </SortableContext>
      </DndContext>
    </Drawer>
  );
}
